import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useDashboardUsers } from '@/hooks/useDashboardQueries';
import { routes } from '@/routes/paths';
import { SectionHeader } from '@/components/dashboard/DashboardCards';
import { ErrorState } from '@/components/ui/States';
import type { UserOverview, UserOverviewPeriod } from '@/types/dashboard';

const PERIODS: Array<{ id: UserOverviewPeriod; label: string }> = [
  { id: 'overall', label: 'Overall' },
  { id: 'this_year', label: 'This year' },
  { id: 'this_month', label: 'This month' },
  { id: 'this_week', label: 'This week' },
];

type SegmentKey = 'customer' | 'stores' | 'delivery_man';

const SEGMENTS: Array<{
  key: SegmentKey;
  label: string;
  color: string;
  to: string;
}> = [
  { key: 'customer', label: 'Customers', color: '#14b8a6', to: routes.customers },
  { key: 'stores', label: 'Stores', color: '#006B3F', to: routes.stores },
  { key: 'delivery_man', label: 'Deliverymen', color: '#FFC928', to: routes.deliverymen },
];

function countOf(data: UserOverview | undefined, key: SegmentKey): number {
  const v = data?.[key];
  const n = Number(v ?? 0);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Safe percentage from real counts; 0 when total is 0. */
export function userShare(count: number, total: number): number {
  if (!total) return 0;
  return Math.round((count / total) * 1000) / 10;
}

function formatCount(n: number): string {
  return new Intl.NumberFormat().format(n);
}

function UserDonut({
  total,
  segments,
  activeKey,
  onHover,
}: {
  total: number;
  segments: Array<{ key: SegmentKey; label: string; color: string; count: number; share: number }>;
  activeKey: SegmentKey | null;
  onHover: (key: SegmentKey | null) => void;
}) {
  const size = 180;
  const stroke = 24;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const hasData = total > 0;
  let offset = 0;

  return (
    <div className="user-donut-wrap">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`Total users ${total}. ${segments
          .map((s) => `${s.label} ${s.count} (${s.share}%)`)
          .join(', ')}`}
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={hasData ? '#e4efe9' : '#eef5f1'}
          strokeWidth={stroke}
        />
        {hasData
          ? segments.map((s) => {
              const len = (s.count / total) * c;
              const el = (
                <circle
                  key={s.key}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${len} ${c - len}`}
                  strokeDashoffset={-offset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                  opacity={activeKey && activeKey !== s.key ? 0.35 : 1}
                  onMouseEnter={() => onHover(s.key)}
                  onMouseLeave={() => onHover(null)}
                  style={{ transition: 'opacity 0.15s' }}
                >
                  <title>{`${s.label}: ${formatCount(s.count)} users (${s.share}%)`}</title>
                </circle>
              );
              offset += len;
              return el;
            })
          : null}
        <text x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className="user-donut-total">
          {formatCount(total)}
        </text>
        <text x="50%" y="62%" textAnchor="middle" dominantBaseline="middle" className="user-donut-caption">
          Total
        </text>
      </svg>
    </div>
  );
}

/**
 * Compact user distribution panel.
 * Clickable category rows navigate to real management routes.
 */
export function UserDistribution({
  zoneId,
  period,
  onPeriodChange,
}: {
  zoneId: string;
  period: UserOverviewPeriod;
  onPeriodChange: (p: UserOverviewPeriod) => void;
}) {
  const [hoverKey, setHoverKey] = useState<SegmentKey | null>(null);
  const query = useDashboardUsers({ zone_id: zoneId, user_overview: period });
  const users = query.data?.data.userOverview;

  const segments = useMemo(() => {
    const counts = {
      customer: countOf(users, 'customer'),
      stores: countOf(users, 'stores'),
      delivery_man: countOf(users, 'delivery_man'),
    };
    const total = counts.customer + counts.stores + counts.delivery_man;
    return SEGMENTS.map((s) => ({
      ...s,
      count: counts[s.key],
      share: userShare(counts[s.key], total),
    }));
  }, [users]);

  const total = segments.reduce((a, s) => a + s.count, 0);
  const isEmpty = !query.isLoading && !query.error && total === 0;

  return (
    <section className="card user-dist-card" aria-label="User distribution">
      <SectionHeader
        title="User Distribution"
        aside={
          <select
            className="form-control form-control-sm chart-select"
            value={period}
            onChange={(e) => onPeriodChange(e.target.value as UserOverviewPeriod)}
            aria-label="User distribution period"
          >
            {PERIODS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        }
      />

      {query.isLoading ? (
        <div className="user-dist-body" aria-busy="true">
          <div className="user-donut-wrap">
            <div className="skeleton" style={{ width: 120, height: 120, borderRadius: '50%' }} />
          </div>
          <ul className="user-stats-legend">
            {SEGMENTS.map((s) => (
              <li key={s.key} className="user-stats-row">
                <span className="user-dot" style={{ background: '#d7e6dd' }} />
                <span className="skeleton" style={{ height: 14, flex: 1 }} />
              </li>
            ))}
          </ul>
        </div>
      ) : query.error ? (
        <ErrorState description="Unable to load user statistics." onRetry={() => void query.refetch()} />
      ) : (
        <div className="user-dist-body">
          <UserDonut total={total} segments={segments} activeKey={hoverKey} onHover={setHoverKey} />
          {isEmpty ? (
            <p className="user-stats-empty muted">No user data available</p>
          ) : null}
          <ul className="user-stats-legend">
            {segments.map((s) => (
              <li key={s.key}>
                <Link
                  to={s.to as never}
                  className="user-stats-row"
                  onMouseEnter={() => setHoverKey(s.key)}
                  onMouseLeave={() => setHoverKey(null)}
                  onFocus={() => setHoverKey(s.key)}
                  onBlur={() => setHoverKey(null)}
                  title={`${s.label}: ${formatCount(s.count)} users (${s.share}%)`}
                >
                  <span className="user-dot" style={{ background: s.color }} />
                  <span className="user-stats-label">{s.label}</span>
                  <span className="user-stats-count">{formatCount(s.count)}</span>
                  <span className="user-stats-share">{s.share}%</span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sr-only">
            Total users {total}.{' '}
            {segments.map((s) => `${s.label} ${s.count} (${s.share}%)`).join('. ')}.
          </p>
        </div>
      )}
    </section>
  );
}

/** Back-compat wrapper used by older imports. */
export function UserStatisticsCard({ zoneId }: { zoneId: string }) {
  const [period, setPeriod] = useState<UserOverviewPeriod>('overall');
  return <UserDistribution zoneId={zoneId} period={period} onPeriodChange={setPeriod} />;
}
